/**
 * AdminPasswordModal — Fév 2026
 * Permet à l&apos;administrateur de changer son propre mot de passe MASTER.
 * L&apos;ancien mot de passe est obligatoire (defense-in-depth).
 * Endpoint : POST /api/caisse/admin/change-password
 */
import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPasswordModal = ({ open, onOpenChange, onChanged }) => {
  const [oldPw, setOldPw] = React.useState("");
  const [newPw, setNewPw] = React.useState("");
  const [confirmPw, setConfirmPw] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setOldPw("");
      setNewPw("");
      setConfirmPw("");
      setLoading(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!oldPw) {
      toast.error("Merci de saisir l'ancien mot de passe");
      return;
    }
    if (newPw.length < 6) {
      toast.error("Le nouveau mot de passe doit faire au moins 6 caractères");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("La confirmation ne correspond pas");
      return;
    }
    if (newPw === oldPw) {
      toast.error("Le nouveau mot de passe doit être différent de l'ancien");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/caisse/admin/change-password`, {
        old_password: oldPw,
        new_password: newPw,
      });
      toast.success("Mot de passe administrateur changé avec succès");
      onOpenChange(false);
      if (onChanged) onChanged();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Erreur";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-amber-500/40 text-white sm:max-w-md" data-testid="admin-password-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-300">
            <KeyRound className="w-5 h-5" strokeWidth={2.2} />
            Changer le mot de passe administrateur
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Après changement, seul le nouveau mot de passe permettra de se connecter en admin. Les mots de passe par défaut seront désactivés.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 flex items-start gap-2 text-xs text-amber-100">
          <ShieldAlert className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" strokeWidth={2.4} />
          <div>
            Notez bien votre nouveau mot de passe. En cas de perte, l&apos;accès admin ne pourra être restauré que par une intervention technique en base de données.
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Ancien mot de passe</Label>
            <Input
              type="password"
              value={oldPw}
              onChange={(e) => setOldPw(e.target.value)}
              className="bg-slate-700 border-slate-600"
              autoComplete="current-password"
              autoFocus
              data-testid="admin-old-password-input"
              placeholder="Ancien mot de passe"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Nouveau mot de passe <span className="text-slate-500">(6 caractères min.)</span></Label>
            <Input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="bg-slate-700 border-slate-600"
              autoComplete="new-password"
              data-testid="admin-new-password-input"
              placeholder="Nouveau mot de passe"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Confirmer le nouveau mot de passe</Label>
            <Input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="bg-slate-700 border-slate-600"
              autoComplete="new-password"
              data-testid="admin-confirm-password-input"
              placeholder="Répétez le nouveau mot de passe"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              disabled={loading}
              data-testid="admin-password-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
              disabled={loading}
              data-testid="admin-password-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}
              {loading ? "Enregistrement…" : "Changer le mot de passe"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPasswordModal;
